import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Lightning, ChartLineUp, GitFork, ArrowRight } from '@phosphor-icons/react'
import { getUserStats, type UserStats } from '../api'
import { useAuth } from '../auth'
import StreakCelebration from './StreakCelebration'

export default function StreaksCard() {
  const auth = useAuth()
  const [stats, setStats] = useState<UserStats | null>(null)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (!auth.user) {
      setLoaded(true)
      return
    }
    let cancelled = false
    getUserStats()
      .then((s) => {
        if (!cancelled) setStats(s)
      })
      .catch(() => {
        if (!cancelled) setStats(null)
      })
      .finally(() => {
        if (!cancelled) setLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [auth.user])

  // Only render for authenticated users with at least one tracked event
  if (!auth.user || !loaded) return null
  if (!stats || (stats.total_simulations === 0 && stats.total_decisions === 0)) return null

  const streakIsActive = stats.weekly_streak >= 2
  const fireBg = streakIsActive ? 'bg-primary text-white' : 'bg-surface'

  return (
    <section
      className="card-brutal p-5 sm:p-6 mb-8"
      data-testid="streaks-card"
      aria-label="Your LifeOdds stats"
    >
      <StreakCelebration weeklyStreak={stats.weekly_streak} userId={auth.user?.id} />
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="chip border-secondary text-secondary-ink inline-flex">
            <Lightning size={12} weight="fill" className="mr-1" /> Streaks & rolls
          </div>
          <h2 className="font-heading text-xl sm:text-2xl font-black tracking-tight mt-2 leading-tight">
            {streakIsActive ? `${stats.weekly_streak}-week roll streak.` : 'Your decision dice roll.'}
          </h2>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div
          className={`rounded-xl border-2 border-edge p-3 shadow-brutal-sm ${fireBg}`}
          data-testid="stat-weekly-streak"
        >
          <div className="text-xs font-bold uppercase tracking-wider opacity-80">Weekly streak</div>
          <div className="font-heading text-3xl font-black mt-1 leading-none">
            {stats.weekly_streak}
            <span className="text-sm opacity-70 ml-1">wk</span>
          </div>
          <div className="text-xs opacity-70 mt-1">Longest: {stats.longest_streak} wk</div>
        </div>

        <div className="rounded-xl border-2 border-edge p-3 shadow-brutal-sm bg-surface" data-testid="stat-sims">
          <div className="text-xs font-bold uppercase tracking-wider text-ink-muted">Simulations</div>
          <div className="font-heading text-3xl font-black mt-1 leading-none">{stats.total_simulations}</div>
          <div className="text-xs text-ink-muted mt-1">{stats.total_decisions} decision{stats.total_decisions === 1 ? '' : 's'}</div>
        </div>

        <div className="rounded-xl border-2 border-edge p-3 shadow-brutal-sm bg-surface" data-testid="stat-shares">
          <div className="text-xs font-bold uppercase tracking-wider text-ink-muted flex items-center gap-1">
            <ChartLineUp size={12} weight="fill" /> Public shares
          </div>
          <div className="font-heading text-3xl font-black mt-1 leading-none">{stats.total_shares}</div>
          <div className="text-xs text-ink-muted mt-1">{stats.total_views} total views</div>
        </div>

        {stats.most_forked ? (
          <Link
            to={`/share/${stats.most_forked.share_id}`}
            data-testid="stat-most-forked"
            className="rounded-xl border-2 border-edge p-3 shadow-brutal-sm bg-accent/15 hover:bg-accent/30 hover:-translate-y-0.5 transition-all block group"
          >
            <div className="text-xs font-bold uppercase tracking-wider text-ink-muted flex items-center gap-1">
              <GitFork size={12} weight="fill" /> Most forked
            </div>
            <div className="font-heading text-2xl font-black mt-1 leading-none">
              {stats.most_forked.fork_count}
              <span className="text-sm font-bold opacity-70 ml-1">fork{stats.most_forked.fork_count === 1 ? '' : 's'}</span>
            </div>
            <div className="text-xs text-ink-muted mt-1 truncate group-hover:text-primary-ink inline-flex items-center gap-1">
              {stats.most_forked.decision_name} <ArrowRight size={10} weight="bold" />
            </div>
          </Link>
        ) : (
          <div className="rounded-xl border-2 border-dashed border-edge p-3 bg-surface/60" data-testid="stat-most-forked-empty">
            <div className="text-xs font-bold uppercase tracking-wider text-ink-muted flex items-center gap-1">
              <GitFork size={12} weight="fill" /> Most forked
            </div>
            <div className="font-heading text-base font-bold mt-1 leading-tight">No forks yet</div>
            <div className="text-xs text-ink-muted mt-1">Share a decision to start.</div>
          </div>
        )}
      </div>
    </section>
  )
}
